import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { quantileLabel } from '@/lib/quantiles';
import type { AneelInterval } from '@/services/types';

/**
 * One step's duration ladder, or its refusal.
 *
 * Shared by the enforcement timeline and the intimation track, which are two
 * corpora under one ladder shape: `labelKey` names the locale block holding
 * the step titles, and the day unit and the impossible-ordering sentence are
 * the timeline's, because they mean the same thing on both.
 *
 * The impossible count is stated on the interval it belongs to, not once for
 * the page: the counts differ per step, and a reader looking at one ladder
 * needs to know whether THIS one contains backwards dates.
 */
export function IntervalCard({
  step,
  labelKey,
  impossibleKey = 'aneel:timeline.intervals.impossible',
}: {
  step: AneelInterval;
  labelKey: string;
  /** The plural-aware sentence for backwards dates; the timeline's names cases,
      the inspection record's names notifications. */
  impossibleKey?: string;
}) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 });

  return (
    <div className="rounded-md border bg-background p-3" data-interval={step.stage}>
      <h3 className="mb-2 text-xs font-medium text-foreground">
        {t(`${labelKey}.${step.stage}`, step.stage)}
      </h3>
      <Answer answer={step.summary} help={t(`${labelKey}.help`)}>
        {(summary) => (
          <div className="space-y-2">
            {/* The N and the extremes beside the ladder: a quantile without its
                observation count is a shape with no weight behind it. */}
            <p className="text-xs text-muted-foreground" data-observations={summary.n}>
              {/* The step block may name its own subject (notifications); the
                  timeline's "cases" is the fallback for the tracks that are cases. */}
              {t([`${labelKey}.observations`, 'aneel:timeline.intervals.observations'], {
                n: number.format(summary.n),
              })}
            </p>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-5">
              {Object.entries(summary.quantiles).map(([key, value]) => (
                <div key={key}>
                  <dt className="text-muted-foreground">{quantileLabel(t, key)}</dt>
                  <dd className="font-medium tabular-nums text-foreground">
                    {number.format(value)} {t('aneel:timeline.intervals.days')}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-muted-foreground" data-extremes>
              {t('aneel:timeline.intervals.extremes', {
                minimum: number.format(summary.minimum),
                maximum: number.format(summary.maximum),
              })}
            </p>
            {step.impossible > 0 ? (
              <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-foreground">
                {/* `count`, not a preformatted string: i18next selects the
                    plural form from `count` alone. */}
                {t(impossibleKey, { count: step.impossible })}
              </p>
            ) : null}
          </div>
        )}
      </Answer>
    </div>
  );
}
